import React, { useReducer, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { AddRuleDialog } from "@dashboard/policies/components/AddRuleDialog/AddRuleDialog.lazy";
import { redactSecrets } from "@dashboard/policies/rules/redact";
import RuleCatalog from "@dashboard/policies/rules/RuleCatalog";
import { StatusIcon } from "@dashboard/shared/components/StatusIcon";
import { ConfirmDialog } from "@devices/components/shared";
import { MdmPolicyCommand } from "@devices/types";
import { Box, Collapse, DialogContentText, Typography } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { DragHandle } from "@shared/DragHandle";
import { DropIndicator } from "@shared/DropIndicator";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { MinimalTableDetailsRow } from "@shared/table/CollapsibleRow";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";

import { EditPolicyRuleDialog } from "../EditPolicyRuleDialog/EditPolicyRuleDialog";
import { getPolicyRuleLabels } from "../policyRuleLabels";

import { useStyles } from "./PolicyRuleItem.style";

export type PolicyRuleDrag = {
    rowRef: React.RefObject<HTMLTableRowElement | null>;
    handleRef: React.RefObject<HTMLSpanElement | null>;
    isDragging: boolean;
    dropEdge: Edge | null;
};

/**
 * Saves the policy's rules and reports whether the save went through.
 * `silent` leaves the success toast out, for a change the table already shows by itself.
 */
export type PolicyRulesUpdate = (content: MdmPolicyCommand[], options?: { silent?: boolean }) => Promise<boolean>;

interface PolicyRuleItemProps {
    policyCommand: MdmPolicyCommand;
    content: MdmPolicyCommand[];
    index: number;
    duplicated?: boolean;
    onUpdate?: PolicyRulesUpdate;
    readOnly?: boolean;
    loading?: boolean;
    rowRef?: PolicyRuleDrag["rowRef"];
    handleRef?: PolicyRuleDrag["handleRef"];
    isDragging?: boolean;
    dropEdge?: Edge | null;
}

const PolicyRuleItem = ({ rowRef, handleRef, isDragging = false, dropEdge = null, ...props }: PolicyRuleItemProps) => {
    const [editDialogOpen, setEditDialogOpen] = useReducer((state) => !state, false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [open, setOpen] = useState(-1);

    const { classes, cx } = useStyles();
    const { t } = useTranslation(["modals", "sidepanels", "pages"]);

    const dropIndicator = dropEdge ? <DropIndicator edge={dropEdge} /> : null;
    const dragCellClassName = rowRef ? classes.dragCell : undefined;

    const readOnly = props.readOnly ?? false;
    const loading = props.loading ?? false;

    const isOpen = open === props.index;

    const matchedRule = RuleCatalog.fromCommand(props.policyCommand);
    const commandLabels = getPolicyRuleLabels(props.policyCommand, matchedRule);

    const toggleModal = () => setIsDeleteModalOpen((prev) => !prev);

    const toggleOpen = () => setOpen(open === props.index ? -1 : props.index);

    const handleDelete = async (policy: MdmPolicyCommand) => {
        if (!props.onUpdate) {
            return;
        }
        const filteredContent = props.content.filter((c) => c !== policy);
        await props.onUpdate(filteredContent);
    };

    const replaceCommand = async (json: Record<string, unknown>) => {
        if (!props.onUpdate) {
            return false;
        }
        const content = props.content.map((c) => (c === props.policyCommand ? (json as MdmPolicyCommand) : c));
        const updatedContent = await props.onUpdate(content);
        return !!updatedContent;
    };

    return (
        <React.Fragment>
            <HoverRow ref={rowRef} clickable={false} className={cx(isDragging && classes.dragging)}>
                <MinimalTableCell align="left" className={cx(dragCellClassName, isOpen && classes.noBottomBorder)}>
                    {dropIndicator}
                    <Box className={classes.indexCellContent}>
                        {!readOnly && handleRef ? (
                            <DragHandle
                                ref={handleRef}
                                className={classes.dragHandle}
                                title={t("pages:policiesPage.tooltip.dragToMove")}
                            />
                        ) : null}
                        <ExpandToggleButton expanded={isOpen} onClick={toggleOpen} className={classes.collapseBtn} />
                    </Box>
                </MinimalTableCell>

                <MinimalTableCell align="center" width={20} className={cx(dragCellClassName, isOpen && classes.noBottomBorder)}>
                    {dropIndicator}
                    {props.duplicated ? (
                        <StatusIcon status="warning" tooltip={t("pages:policiesPage.tooltip.duplicatedRule")} />
                    ) : null}
                </MinimalTableCell>

                <MinimalTableCell
                    align="left"
                    className={cx(dragCellClassName, classes.ruleCell, isOpen && classes.noBottomBorder)}
                >
                    {dropIndicator}
                    <Typography noWrap>{commandLabels.primary}</Typography>
                    {commandLabels.secondary ? (
                        <Typography variant="caption" color="textSecondary" display="block" noWrap>
                            {commandLabels.secondary}
                        </Typography>
                    ) : null}
                </MinimalTableCell>

                <MinimalTableCell align="right" className={cx(dragCellClassName, isOpen && classes.noBottomBorder)}>
                    {dropIndicator}
                    {/*
                     * A save in flight only disables these, it does not spin them: the dialog that started it shows
                     * the spinner on its own button, and a spinner on every row reads as if each one were busy.
                     */}
                    {!readOnly ? (
                        <div className={classes.buttonBox}>
                            <SecondaryButton
                                tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.edit") }}
                                onClick={setEditDialogOpen}
                                className={loading ? classes.iconDisabled : ""}
                                disabled={loading}
                            >
                                <i className="fa-solid fa-fw fa-pencil" />
                            </SecondaryButton>
                            <DangerOutlinedButton
                                onClick={() => setIsDeleteModalOpen(true)}
                                tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.delete") }}
                                className={loading ? classes.iconDisabled : ""}
                                disabled={loading}
                            >
                                <i className="fa-solid fa-fw fa-trash" />
                            </DangerOutlinedButton>
                        </div>
                    ) : null}
                </MinimalTableCell>
            </HoverRow>

            <MinimalTableDetailsRow colSpan={4} cellClassName={classes.customCollapse}>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <div className={classes.collapsingRowContent}>
                        <br />
                        <div>
                            <pre>{JSON.stringify(redactSecrets(props.policyCommand, matchedRule), null, 2)}</pre>
                        </div>
                    </div>
                </Collapse>
            </MinimalTableDetailsRow>

            {props.onUpdate && matchedRule && editDialogOpen ? (
                <AddRuleDialog
                    title={t("modals:editPolicyRule.title")}
                    devices={[]}
                    editedRule={matchedRule}
                    toggleDialog={setEditDialogOpen}
                    createCommand={replaceCommand}
                    loading={loading}
                />
            ) : null}

            {props.onUpdate && !matchedRule ? (
                <EditPolicyRuleDialog
                    content={props.content}
                    onUpdate={props.onUpdate}
                    policyCommand={props.policyCommand}
                    isOpen={editDialogOpen}
                    toggleDialog={setEditDialogOpen}
                />
            ) : null}

            {readOnly ? null : (
                <ConfirmDialog
                    title={t("modals:deleteRule.title")}
                    dangerButton
                    primaryActionText={t("modals:deleteRule.primaryActionText")}
                    open={isDeleteModalOpen}
                    onConfirm={() => handleDelete(props.policyCommand)}
                    onClose={toggleModal}
                >
                    <DialogContentText>
                        <Trans
                            ns="modals"
                            i18nKey="deleteRule.description"
                            values={{ name: commandLabels.primary }}
                            components={{ bold: <strong /> }}
                        />
                    </DialogContentText>
                </ConfirmDialog>
            )}
        </React.Fragment>
    );
};

export default PolicyRuleItem;
